// Option D — what actually moved.
// The 90-day programme is judged on change, not on a single column of numbers,
// so movers are ranked first and the full stage grid sits underneath.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TrendingDown, TrendingUp } from "lucide-react";
import { Section, Score, Delta, Empty, Label } from "@/components/clinical/kit";
import { MATERIAL_DROP, presentStages } from "@/config/clinical";
import { interpret } from "@/lib/status";

function MoverList({ title, icon: Icon, tone, items, field }) {
  const { t } = useTranslation();
  if (!items.length) return null;
  return (
    <div className={`rounded-lg border p-3 ${tone}`}>
      <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-stone-800">
        <Icon className="h-4 w-4" /> {t(title)}
      </div>
      <ul className="space-y-1">
        {items.map((d) => (
          <li key={d.wid} className="flex items-center gap-2 text-sm" data-testid={`mover-${d.wid}`}>
            <span className="font-mono text-[11px] text-stone-400">{d.num}</span>
            <span className="min-w-0 flex-1 truncate text-stone-700">{t(d.label)}</span>
            <span className="font-mono text-xs text-stone-500 tabular-nums">
              {d.first} → {d.now}
            </span>
            <Delta value={d[field]} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ChangePanel({ byStage, changes, overallByStage }) {
  const { t } = useTranslation();
  const [onlyMoved, setOnlyMoved] = useState(false);
  const stages = presentStages(byStage);
  const scored = changes.filter((d) => d.now != null || d.first != null);

  if (stages.length === 0) return (
    <Section title="What changed" sub="Baseline against the latest stage" testid="change-panel">
      <Empty>No stage has a scored domain yet.</Empty>
    </Section>
  );

  const withTotal = scored.filter((d) => d.total != null);
  const improved = withTotal.filter((d) => d.total > 0).sort((a, b) => b.total - a.total).slice(0, 5);
  const declined = withTotal.filter((d) => d.total < 0).sort((a, b) => a.total - b.total).slice(0, 5);
  const material = withTotal.filter((d) => d.total <= MATERIAL_DROP);
  const rows = onlyMoved ? scored.filter((d) => d.total != null && Math.abs(d.total) >= 0.05) : scored;

  return (
    <Section title="What changed" sub="Each domain from its first scored stage to the latest"
      testid="change-panel"
      right={
        <label className="flex items-center gap-1.5 text-xs text-stone-600">
          <input type="checkbox" checked={onlyMoved} onChange={(e) => setOnlyMoved(e.target.checked)}
            data-testid="only-moved" className="h-3.5 w-3.5 accent-emerald-700" />
          {t("Only domains that moved")}
        </label>
      }>
      {stages.length === 1 ? (
        <p className="mb-3 text-sm text-stone-500">
          {t("Only")} <b>{t(stages[0])}</b> {t("has been scored, so there is nothing to compare yet.")}
        </p>
      ) : (
        <>
          {material.length > 0 && (
            <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
              data-testid="material-drop">
              <b>{material.length}</b>{" "}
              {t(material.length === 1 ? "domain has fallen" : "domains have fallen")} {Math.abs(MATERIAL_DROP)}+{" "}
              {t("points since the first assessment")} — {material.map((d) => t(d.label)).join(", ")}.
            </div>
          )}
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <MoverList title="Most improved" icon={TrendingUp} tone="border-emerald-200 bg-emerald-50/60"
              items={improved} field="total" />
            <MoverList title="Deteriorated" icon={TrendingDown} tone="border-rose-200 bg-rose-50/60"
              items={declined} field="total" />
          </div>
        </>
      )}

      <div className="overflow-x-auto rounded-md border border-[#E5DFD3]">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-[#E8EFE9] text-left">
            <tr>
              <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-stone-500">{t("Domain")}</th>
              {stages.map((s) => (
                <th key={s} className="px-3 py-2 text-center font-mono text-[11px] uppercase tracking-wider text-stone-500">{t(s)}</th>
              ))}
              <th className="px-3 py-2 text-center font-mono text-[11px] uppercase tracking-wider text-stone-500">Δ</th>
              <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-stone-500">{t("Interpretation")}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t-2 border-[#CAD2C5] bg-[#F6F8F5] font-semibold" data-testid="overall-row">
              <td className="px-3 py-2 text-stone-800">{t("Overall domain average")}</td>
              {stages.map((s) => (
                <td key={s} className="px-3 py-2 text-center">
                  <Score value={overallByStage?.[s]} color={interpret(overallByStage?.[s]).color} size="sm" />
                </td>
              ))}
              <td className="px-3 py-2 text-center">
                <Delta testid="overall-delta" value={
                  stages.length > 1 && overallByStage?.[stages[0]] != null && overallByStage?.[stages[stages.length - 1]] != null
                    ? Math.round((overallByStage[stages[stages.length - 1]] - overallByStage[stages[0]]) * 10) / 10
                    : null} />
              </td>
              <td className="px-3 py-2 text-xs text-stone-500">
                {t(interpret(overallByStage?.[stages[stages.length - 1]]).label)}
              </td>
            </tr>
            {rows.map((d) => {
              const it = interpret(d.now);
              return (
                <tr key={d.wid} className="border-t border-[#EFE9DE]" data-testid={`change-row-${d.wid}`}>
                  <td className="px-3 py-1.5 text-stone-700">
                    <span className="mr-2 font-mono text-[11px] text-stone-400">{d.num}</span>{t(d.label)}
                  </td>
                  {stages.map((s) => {
                    const v = byStage[s]?.[d.wid];
                    return (
                      <td key={s} className="px-3 py-1.5 text-center font-mono tabular-nums"
                        style={{ color: v == null ? "#D6D3D1" : interpret(v).color }}>
                        {v ?? "—"}
                      </td>
                    );
                  })}
                  <td className="px-3 py-1.5 text-center"><Delta value={d.total} /></td>
                  <td className="px-3 py-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${it.bg} ${it.badge}`}>
                      {t(it.label)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && <div className="mt-2"><Empty>No domain has moved.</Empty></div>}
      <p className="mt-2 text-[11px] text-stone-400">
        <Label>Reading</Label> {t("Higher is better. Δ is the latest stage minus the first stage that scored the domain.")}
      </p>
    </Section>
  );
}
